import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/blog`)
      .then((res) => setPosts(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const categories = Array.from(new Set(posts.map((p) => p.category).filter(Boolean)));
  const visiblePosts = activeCategory ? posts.filter((p) => p.category === activeCategory) : posts;

  return (
    <div data-testid="page-blog">
      <SEO title="Blog" description="Considered writing from Gopi Gadhvi Group and its brands." path="/blog" />
      <PageHero eyebrow="From the Journal" heading="Blog" intro="Considered writing from Gopi Gadhvi Group and the brands within it." />

      <section className="relative page-shell pb-28">
        {categories.length > 0 && (
          <div className="flex items-center gap-3 mb-12 flex-wrap" data-testid="blog-category-filter">
            <button
              onClick={() => setActiveCategory(null)}
              className={`eyebrow border rounded-full px-4 py-2 transition-colors duration-200 ${!activeCategory ? "" : "opacity-60"}`}
              style={{ borderColor: !activeCategory ? "var(--gg-logo-gold)" : "var(--gg-fine-border)", color: !activeCategory ? "var(--gg-logo-gold)" : undefined }}
              data-testid="blog-category-all"
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`eyebrow border rounded-full px-4 py-2 transition-colors duration-200 ${activeCategory === cat ? "" : "opacity-60"}`}
                style={{ borderColor: activeCategory === cat ? "var(--gg-logo-gold)" : "var(--gg-fine-border)", color: activeCategory === cat ? "var(--gg-logo-gold)" : undefined }}
                data-testid={`blog-category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-loading">
            {Array.from({ length: 6 }).map((_, i) => <div key={i} className="neu-surface h-[320px] animate-pulse" />)}
          </div>
        )}

        {!loading && error && (
          <div className="neu-panel p-10 text-center" data-testid="blog-error">
            <p className="text-body">We could not load the journal right now. Please try again shortly.</p>
          </div>
        )}

        {!loading && !error && visiblePosts.length === 0 && (
          <div className="neu-panel p-10 text-center" data-testid="blog-empty">
            <p className="text-body">New stories are being written. Please check back soon.</p>
          </div>
        )}

        {!loading && !error && visiblePosts.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
            {visiblePosts.map((post, i) => (
              <Reveal key={post.id} delay={(i % 6) * 70}>
                <Link to={`/blog/${post.slug}`} className="neu-surface neu-surface-hover block h-full overflow-hidden" data-testid={`blog-card-${post.slug}`}>
                  {post.cover_image && (
                    <div className="h-[200px] overflow-hidden">
                      <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="p-6">
                    <span className="eyebrow" style={{ color: "var(--gg-muted-text)" }}>{post.category}</span>
                    <h3 className="text-h4 mt-3 mb-2">{post.title}</h3>
                    <p className="text-body">{post.excerpt}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Blog;
